/**
 * Tools helper module for working with tools in the Rookoo Portal
 * Provides utility functions for fetching, caching, and working with tools
 */

// Helper function to get tool icon based on name
export const getToolIcon = toolName => {
  const iconMap = {
    build_query: '🔍',
    lookup_information: '❓',
    recommend: '💡',
    start_intake: '📋',
    escalate: '⬆️',
    ask_user: 'i-lucide-message-square-text',
  };
  return iconMap[toolName] || '🔧';
};

// Format tools for UI display
export const formatToolsForUI = tools => {
  if (!tools) return [];

  const predefinedTools = tools.predefined || [];
  const mcpTools = tools.mcp || [];

  return [
    ...predefinedTools.map(tool => ({
      id: tool.name,
      title: tool.name,
      description: tool.description,
      category: tool.type === 'mcp' ? 'MCP' : 'Data',
      icon: getToolIcon(tool.name),
      itemType: 'tool',
      type: tool.type,
    })),
    ...mcpTools.map(tool => ({
      id: tool.name,
      title:
        metaDisplayName(tool.meta) ||
        deriveDisplayNameFromToolName(tool.name) ||
        tool.name,
      label:
        metaDisplayName(tool.meta) ||
        deriveDisplayNameFromToolName(tool.name) ||
        tool.name,
      description: tool.description,
      category: 'MCP',
      icon: '🔗',
      itemType: 'tool',
      type: tool.type,
    })),
  ];
};

// Humanize integration key for display (e.g. "teamleader" -> "Teamleader")
const integrationLabel = key =>
  key && key.length
    ? key.charAt(0).toUpperCase() + key.slice(1).toLowerCase()
    : key;

export const isInternalRookooArgument = name =>
  typeof name === 'string' && name.startsWith('INTERNAL_ROOKOO_');

export const filterInternalRookooArguments = toolArguments =>
  (toolArguments || []).filter(arg => !isInternalRookooArgument(arg?.name));

export const visibleInputSchemaProperties = inputSchema => {
  if (!inputSchema?.properties) return {};

  return Object.fromEntries(
    Object.entries(inputSchema.properties).filter(
      ([name]) => !isInternalRookooArgument(name)
    )
  );
};

export const isInputSchemaPropertyRequired = (inputSchema, propName) =>
  Boolean(inputSchema?.required?.includes(propName));

// Read display name from meta (API may use "name" or "Name"); prefer meta when present
export function metaDisplayName(meta) {
  if (!meta || typeof meta !== 'object') return null;
  return meta.name ?? meta.Name ?? null;
}

function metaIntegration(meta) {
  if (!meta || typeof meta !== 'object') return null;
  return meta.integration ?? meta.Integration ?? null;
}

// Integration prefixes that get their own section (teamleader_*, hubspot_*). Others go under "Default".
const INTEGRATION_PREFIXES = ['teamleader', 'hubspot'];

// Derive integration key from tool name when meta.integration is missing. Only teamleader/hubspot get their own group; others return null (caller will use "default").
function deriveIntegrationFromName(toolName) {
  if (!toolName || typeof toolName !== 'string') return null;
  const lower = toolName.toLowerCase();
  for (const prefix of INTEGRATION_PREFIXES) {
    if (lower.startsWith(`${prefix}_`)) return prefix;
  }
  return null;
}

// Humanize snake_case identifiers for display (e.g. find_available_slots -> Find available slots)
export function humanizeSnakeCase(value) {
  if (!value || typeof value !== 'string') return value;
  const withSpaces = value.replace(/_/g, ' ').trim();
  if (!withSpaces) return value;
  return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1).toLowerCase();
}

// Humanize tool name for display when meta.name is missing (e.g. teamleader_companies_list -> Companies list)
export function deriveDisplayNameFromToolName(toolName) {
  if (!toolName || typeof toolName !== 'string') return toolName;
  const integration = deriveIntegrationFromName(toolName);
  let rest = toolName;
  if (integration && toolName.toLowerCase().startsWith(`${integration}_`)) {
    rest = toolName.slice(integration.length + 1);
  }
  // Remove trailing __suffix (e.g. hubspot_submit__b18ef224_ -> hubspot_submit)
  rest = rest.replace(/__[a-z0-9_]+$/i, '');
  return humanizeSnakeCase(rest) || toolName;
}

// Same display-name resolution as ToolboxSidebar / groupToolsByMCPServer
export function getToolDisplayName(tool) {
  if (!tool) return '';
  return (
    metaDisplayName(tool.meta) ||
    metaDisplayName(tool.metadata) ||
    metaDisplayName(tool._meta) ||
    deriveDisplayNameFromToolName(tool.name) ||
    tool.name ||
    ''
  );
}

// Group tools by MCP instance (server), then by integration within each server
export const groupToolsByMCPServer = tools => {
  if (!tools) return { general: [], mcpServers: [] };

  const predefinedTools = tools.predefined || [];
  const mcpTools = tools.mcp || [];

  const serverMap = {};

  mcpTools.forEach(tool => {
    const displayName =
      metaDisplayName(tool.meta) ||
      metaDisplayName(tool.metadata) ||
      deriveDisplayNameFromToolName(tool.name) ||
      tool.name;
    const integrationKey =
      metaIntegration(tool.meta) ||
      metaIntegration(tool.metadata) ||
      deriveIntegrationFromName(tool.name);
    const key = integrationKey
      ? String(integrationKey).toLowerCase()
      : 'default';
    const integrationLabelForGroup = integrationKey
      ? integrationLabel(integrationKey)
      : 'Default';

    const toolPayload = {
      id: tool.name,
      title: displayName,
      label: displayName,
      description: tool.description,
      category: 'MCP',
      icon: '🔗',
      itemType: 'tool',
      type: tool.type,
      meta: tool.meta,
    };

    const serverId = tool.mcp?.server_id || 'unknown';
    const serverLabel =
      tool.mcp?.label || tool.mcp?.server_label || 'Unknown Server';

    if (!serverMap[serverId]) {
      serverMap[serverId] = {
        serverId,
        serverLabel,
        serverUrl: tool.mcp?.internal ? null : tool.mcp?.uri,
        internal: Boolean(tool.mcp?.internal),
        integrations: {},
      };
    }

    const integrations = serverMap[serverId].integrations;
    if (!integrations[key]) {
      integrations[key] = {
        integrationKey: key,
        integrationLabel: integrationLabelForGroup,
        tools: [],
      };
    }
    integrations[key].tools.push(toolPayload);
  });

  const mcpServers = Object.values(serverMap).map(server => ({
    ...server,
    integrations: Object.values(server.integrations).sort((a, b) =>
      a.integrationLabel.localeCompare(b.integrationLabel, undefined, {
        sensitivity: 'base',
      })
    ),
  }));

  return {
    general: predefinedTools.map(tool => ({
      id: tool.name,
      title: tool.label || humanizeSnakeCase(tool.name),
      label: tool.label || humanizeSnakeCase(tool.name),
      description: tool.description,
      category: tool.type === 'mcp' ? 'MCP' : 'Data',
      icon: tool.icon || getToolIcon(tool.name),
      itemType: 'tool',
      type: tool.type,
    })),
    mcpServers,
  };
};

// Fetch tools with caching logic
export const fetchToolsWithCache = async store => {
  try {
    // Check if tools in store are fresh before fetching
    const isToolsFresh = store.getters['tools/isAvailableToolsFresh'];
    if (!isToolsFresh) {
      await store.dispatch('tools/getAvailable');
    }

    // Get tools from store
    const allTools = store.getters['tools/getAvailableTools'];
    return formatToolsForUI(allTools);
  } catch (error) {
    console.error('Failed to fetch tools:', error);
    return [];
  }
};

// Filter tools by search query
export const filterToolsByQuery = (tools, query) => {
  if (!query) return tools;

  const searchTerm = query.toLowerCase();
  return tools.filter(item => {
    return (
      (item.title && item.title.toLowerCase().includes(searchTerm)) ||
      (item.description &&
        item.description.toLowerCase().includes(searchTerm)) ||
      (item.category && item.category.toLowerCase().includes(searchTerm)) ||
      (item.id && item.id.toLowerCase().includes(searchTerm)) ||
      (item.itemType && item.itemType.toLowerCase().includes(searchTerm))
    );
  });
};

// Get a tool by name from the store
export const getToolByName = (store, name) => {
  return store.getters['tools/getToolByName'](name);
};

// Execute a tool with error handling
export const executeTool = async (store, toolId, args) => {
  try {
    return await store.dispatch('tools/execute', { toolId, args });
  } catch (error) {
    console.error(`Error executing tool ${toolId}:`, error);
    throw error;
  }
};
